/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback } from "react";
import { BEM } from "@adapptio/lib-components";
import { IInputBaseProps } from "../InputBase";
import { CodeEditor, ICodeEditorProps } from "../CodeEditor";
import { isFunction, isUndefined } from "@adapptio/lib-shared";

import "./CodeInput.scss";

export interface ICodeInputProps extends IInputBaseProps {
	className?: string;
	multiLine?: boolean;
	height?: number;
}

export const CodeInput = React.forwardRef<HTMLInputElement, ICodeInputProps>(
	({ className, defaultValue, onChange, multiLine, height, ...props }, ref) => {
		const baseClass = BEM("edt-code-input");

		if (className) {
			baseClass.add(className);
		}

		const handleChange = useCallback<ICodeEditorProps["onChange"]>((value) => {
			if (isFunction(onChange)) {
				type TOnChange = (value: string) => void;
				(onChange as unknown as TOnChange)(value); // bad typing, hack
			}
		}, []);

		return (
			<div className={baseClass.toClassName()}>
				<input hidden ref={ref} />
				<CodeEditor
					singleLine={!multiLine}
					height={height}
					className={baseClass.element("editor").toClassName()}
					defaultValue={isUndefined(defaultValue) ? undefined : `${defaultValue ?? ""}`}
					onChange={handleChange}
					{...props}
				/>
			</div>
		);
	}
);
